import { useEffect, useState } from "react";
import { adminList, adminCreate, adminUpdate, adminDelete, adminDuplicate, publishPipeline } from "../../lib/api";
import { Plus, Edit2, Trash2, Save, X, Loader2, Copy, Sparkles, Send, Rocket } from "lucide-react";
import ImageUploadField from "./ImageUploadField";
import AIBlogGenerator from "./AIBlogGenerator";

/**
 * Generic content manager.
 */
function FaqItemsEditor({ value, onChange }) {
  const items = Array.isArray(value) ? value : [];
  const update = (i, patch) => {
    const next = items.map((it, idx) => (idx === i ? { ...it, ...patch } : it));
    onChange(next);
  };
  const add = () => onChange([...items, { question: "", answer: "" }]);
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  const move = (i, delta) => {
    const j = i + delta;
    if (j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };
  return (
    <div className="space-y-3" data-testid="faq-items-editor">
      {items.length === 0 && (
        <div className="text-xs text-charcoal-soft">
          No FAQ items yet. They power the in-page FAQ section and the FAQPage JSON-LD schema (rich result eligible).
        </div>
      )}
      {items.map((it, i) => (
        <div key={i} className="border border-black/10 rounded-lg p-3 space-y-2 bg-bone-alt/30" data-testid={`faq-item-${i}`}>
          <input
            type="text"
            value={it.question || ""}
            onChange={(e) => update(i, { question: e.target.value })}
            placeholder="Question (e.g., How long do I have to file?)"
            className="w-full p-2 rounded-md border border-black/10 outline-none focus:border-forest text-sm font-semibold"
            data-testid={`faq-question-${i}`}
          />
          <textarea
            rows={3}
            value={it.answer || ""}
            onChange={(e) => update(i, { answer: e.target.value })}
            placeholder="Answer"
            className="w-full p-2 rounded-md border border-black/10 outline-none focus:border-forest text-sm"
            data-testid={`faq-answer-${i}`}
          />
          <div className="flex gap-1 text-xs">
            <button type="button" onClick={() => move(i, -1)} disabled={i === 0} className="btn-ghost px-2 py-1 disabled:opacity-40">↑</button>
            <button type="button" onClick={() => move(i, 1)} disabled={i === items.length - 1} className="btn-ghost px-2 py-1 disabled:opacity-40">↓</button>
            <button type="button" onClick={() => remove(i)} className="text-red-600 hover:bg-red-50 px-2 py-1 rounded">Remove</button>
          </div>
        </div>
      ))}
      <button type="button" onClick={add} className="btn-ghost text-xs px-3 py-2" data-testid="faq-add-item">
        + Add FAQ item
      </button>
    </div>
  );
}

export default function ContentManager({
  resource, title, description, fields, listFields, defaults = {},
  enableAI = false, enableDuplicate = false, duplicateFields = [],
  enableQuickPublish = false,
  editToolbar = null,  // function(item, refresh) → JSX rendered in the edit form header
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [showAI, setShowAI] = useState(false);
  const [duplicating, setDuplicating] = useState(null);
  const [dupForm, setDupForm] = useState({});
  const [publishingId, setPublishingId] = useState(null);

  const load = () => {
    setLoading(true);
    adminList(resource).then(setItems).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [resource]);

  const startNew = () => { setForm({ ...defaults, published: true }); setEditing("new"); setErr(""); };
  const startEdit = (it) => { setForm({ ...it }); setEditing(it.id); setErr(""); };
  const cancel = () => { setEditing(null); setForm(defaults); setErr(""); };

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const payload = { ...form };
      fields.forEach((f) => {
        if (f.type === "list" && typeof payload[f.key] === "string") {
          payload[f.key] = payload[f.key].split("\n").map((s) => s.trim()).filter(Boolean);
        }
        if (f.type === "tags" && typeof payload[f.key] === "string") {
          payload[f.key] = payload[f.key].split(",").map((s) => s.trim()).filter(Boolean);
        }
        if (f.type === "number") payload[f.key] = parseInt(payload[f.key]) || 0;
      });
      if (editing === "new") await adminCreate(resource, payload);
      else await adminUpdate(resource, editing, payload);
      load();
      cancel();
    } catch (e) {
      setErr(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await adminDelete(resource, id);
    setItems((xs) => xs.filter((x) => x.id !== id));
  };

  const quickPublish = async (it) => {
    if (resource === "blog/posts") {
      if (!window.confirm(`Run the 1-click publish pipeline for "${it.title || it.slug || "this post"}"?\n\nPublishes • generates a hero image + alt text • refreshes internal links • regenerates the sitemap • pings Search Console.`)) return;
      setPublishingId(it.id);
      try {
        const res = await publishPipeline(it.id, { generate_image: true });
        const s = res.steps || {};
        alert(`Published "${it.title || it.slug}"\n\n` + Object.entries(s).map(([k, v]) => `• ${k}: ${v}`).join("\n"));
      } catch (e) {
        alert("Pipeline error: " + (e?.response?.data?.detail || e.message));
      } finally {
        setPublishingId(null);
      }
    } else {
      if (!window.confirm(`Publish "${it.title || it.slug || "this item"}" now?`)) return;
      await adminUpdate(resource, it.id, { status: "published", published: true });
    }
    load();
  };

  const startDuplicate = (it) => {
    const initial = {};
    duplicateFields.forEach((df) => { initial[df.key] = df.placeholder || ""; });
    setDupForm(initial);
    setDuplicating(it);
  };

  const confirmDuplicate = async () => {
    try {
      const overrides = { ...dupForm };
      // auto-slug if not provided
      if (overrides.city_name && !overrides.slug) {
        overrides.slug = overrides.city_name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      }
      if (overrides.name && !overrides.slug) {
        overrides.slug = overrides.name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      }
      if (overrides.title && !overrides.slug) {
        overrides.slug = overrides.title.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
      }
      await adminDuplicate(resource, duplicating.id, overrides);
      setDuplicating(null);
      setDupForm({});
      load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Duplicate failed");
    }
  };

  const onAIComplete = (draft) => {
    setShowAI(false);
    setForm({ ...defaults, ...draft, published: false });
    setEditing("new");
  };

  const formatField = (it, k) => {
    const v = it[k];
    if (Array.isArray(v)) return v.slice(0, 3).join(", ") + (v.length > 3 ? "..." : "");
    if (typeof v === "boolean") return v ? "Yes" : "No";
    if (typeof v === "string" && v.length > 80) return v.slice(0, 80) + "...";
    return v ?? "";
  };

  return (
    <div data-testid={`admin-${resource.replace("/", "-")}-page`}>
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-display font-bold text-forest">{title}</h1>
          {description && <p className="text-charcoal-soft text-sm mt-1">{description}</p>}
        </div>
        {editing === null && (
          <div className="flex gap-2 flex-wrap">
            {enableAI && (
              <button onClick={() => setShowAI(true)} className="btn-secondary text-sm" data-testid="ai-generator-button">
                <Sparkles className="w-4 h-4" /> AI Draft
              </button>
            )}
            <button onClick={startNew} className="btn-primary text-sm" data-testid="content-new-button">
              <Plus className="w-4 h-4" /> New
            </button>
          </div>
        )}
      </div>

      {showAI && <AIBlogGenerator onComplete={onAIComplete} onClose={() => setShowAI(false)} />}

      {duplicating && (
        <div className="fixed inset-0 z-50 grid place-items-center px-4 bg-black/60 backdrop-blur-sm" onClick={() => setDuplicating(null)} data-testid="duplicate-modal">
          <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-md rounded-2xl border border-black/5 shadow-2xl p-6">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-xl bg-forest text-bone grid place-items-center"><Copy className="w-5 h-5" /></div>
              <div>
                <h3 className="font-display font-bold text-xl text-forest">Duplicate Page</h3>
                <p className="text-xs text-charcoal-soft">Creates an unpublished copy you can edit.</p>
              </div>
            </div>
            <div className="space-y-3">
              {duplicateFields.map((f) => (
                <div key={f.key}>
                  <label className="text-sm font-semibold text-charcoal mb-1 block">{f.label}</label>
                  <input
                    type="text"
                    value={dupForm[f.key] || ""}
                    onChange={(e) => setDupForm({ ...dupForm, [f.key]: e.target.value })}
                    placeholder={f.placeholder}
                    className="w-full p-2.5 rounded-lg border border-black/10 focus:border-forest outline-none"
                    data-testid={`duplicate-${f.key}-input`}
                  />
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setDuplicating(null)} className="btn-ghost text-sm" data-testid="duplicate-cancel">Cancel</button>
              <button onClick={confirmDuplicate} className="btn-secondary text-sm" data-testid="duplicate-confirm">
                <Copy className="w-4 h-4" /> Duplicate
              </button>
            </div>
          </div>
        </div>
      )}

      {editing !== null && (
        <div className="bg-white rounded-2xl border border-black/5 p-6 md:p-8 mb-6 shadow-sm" data-testid="content-form">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <h3 className="font-display font-bold text-xl text-forest">
              {editing === "new" ? "Create new" : "Edit item"}
            </h3>
            {editing !== "new" && editToolbar && (
              <div className="flex gap-2 flex-wrap" data-testid="content-edit-toolbar">
                {editToolbar(form, load, setForm)}
              </div>
            )}
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {fields.map((f) => (
              <div key={f.key} className={f.full ? "md:col-span-2" : ""}>
                <label className="text-sm font-semibold text-charcoal mb-1.5 block">{f.label}</label>
                {f.type === "image" ? (
                  <ImageUploadField value={form[f.key] || ""} onChange={(v) => setForm({ ...form, [f.key]: v })} />
                ) : f.type === "textarea" || f.type === "list" ? (
                  <textarea
                    rows={f.rows || 5}
                    value={Array.isArray(form[f.key]) ? form[f.key].join("\n") : (form[f.key] || "")}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full p-3 rounded-lg border border-black/10 focus:border-forest outline-none resize-y font-sans text-sm"
                    placeholder={f.placeholder || ""}
                    data-testid={`field-${f.key}`}
                  />
                ) : f.type === "tags" ? (
                  <input
                    type="text"
                    value={Array.isArray(form[f.key]) ? form[f.key].join(", ") : (form[f.key] || "")}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    placeholder="comma-separated tags"
                    className="w-full p-3 rounded-lg border border-black/10 focus:border-forest outline-none"
                    data-testid={`field-${f.key}`}
                  />
                ) : f.type === "boolean" ? (
                  <label className="flex items-center gap-2 mt-2 text-sm">
                    <input
                      type="checkbox"
                      checked={!!form[f.key]}
                      onChange={(e) => setForm({ ...form, [f.key]: e.target.checked })}
                      className="w-4 h-4 accent-forest"
                      data-testid={`field-${f.key}`}
                    />
                    {f.label}
                  </label>
                ) : f.type === "select" ? (
                  <select
                    value={form[f.key] ?? (f.options?.[0]?.value || "")}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full p-3 rounded-lg border border-black/10 focus:border-forest outline-none bg-white"
                    data-testid={`field-${f.key}`}
                  >
                    {(f.options || []).map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                ) : f.type === "datetime" ? (
                  <input
                    type="datetime-local"
                    value={form[f.key] ? String(form[f.key]).slice(0, 16) : ""}
                    onChange={(e) => setForm({
                      ...form,
                      [f.key]: e.target.value ? new Date(e.target.value).toISOString() : null,
                    })}
                    className="w-full p-3 rounded-lg border border-black/10 focus:border-forest outline-none"
                    data-testid={`field-${f.key}`}
                  />
                ) : f.type === "faqItems" ? (
                  <FaqItemsEditor
                    value={Array.isArray(form[f.key]) ? form[f.key] : []}
                    onChange={(v) => setForm({ ...form, [f.key]: v })}
                  />
                ) : (
                  <input
                    type={f.type || "text"}
                    value={form[f.key] ?? ""}
                    onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                    className="w-full p-3 rounded-lg border border-black/10 focus:border-forest outline-none"
                    placeholder={f.placeholder || ""}
                    data-testid={`field-${f.key}`}
                  />
                )}
                {f.hint && <p className="text-xs text-charcoal-soft mt-1">{f.hint}</p>}
              </div>
            ))}
          </div>

          {err && <p className="text-red-600 text-sm mt-4" data-testid="content-form-error">{err}</p>}

          <div className="flex gap-2 mt-6">
            <button onClick={save} disabled={saving} className="btn-secondary text-sm" data-testid="content-save-button">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-4 h-4" /> Save</>}
            </button>
            <button onClick={cancel} className="btn-ghost text-sm" data-testid="content-cancel-button">
              <X className="w-4 h-4" /> Cancel
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <p className="text-charcoal-soft">Loading...</p>
      ) : items.length === 0 ? (
        <div className="text-center py-16 bg-white rounded-xl border border-black/5">
          <p className="text-charcoal-soft mb-4">No items yet. Create your first one!</p>
          {editing === null && <button onClick={startNew} className="btn-primary">Create new</button>}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-black/5 overflow-hidden">
          {items.map((it, i) => (
            <div
              key={it.id}
              className={`p-5 flex flex-col md:flex-row md:items-center gap-3 ${i > 0 ? "border-t border-black/5" : ""}`}
              data-testid={`content-row-${it.id}`}
            >
              <div className="flex-1 min-w-0">
                {listFields.map((lf) => (
                  <div key={lf.key} className={lf.primary ? "font-display font-bold text-base text-forest" : "text-xs text-charcoal-soft"}>
                    {lf.primary ? formatField(it, lf.key) : (
                      <span>{lf.label}: <span className="text-charcoal">{formatField(it, lf.key)}</span></span>
                    )}
                  </div>
                ))}
                {it.status && (
                  <span
                    className={`inline-block mt-1 mr-1 text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full ${
                      it.status === "published" ? "text-emerald-700 bg-emerald-100" :
                      it.status === "scheduled" ? "text-blue-700 bg-blue-100" :
                      it.status === "in_review" ? "text-purple-700 bg-purple-100" :
                      "text-yellow-700 bg-yellow-100"
                    }`}
                    data-testid={`content-status-${it.id}`}
                  >
                    {it.status === "in_review" ? "In Review" : it.status}
                  </span>
                )}
                {it.scheduled_at && it.status === "scheduled" && (
                  <span className="inline-block mt-1 text-[10px] text-blue-700 ml-1">
                    @ {new Date(it.scheduled_at).toLocaleString()}
                  </span>
                )}
                {!it.status && !it.published && (
                  <span className="inline-block mt-1 text-[10px] uppercase tracking-wider font-bold text-yellow-700 bg-yellow-100 px-2 py-0.5 rounded-full">Unpublished</span>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                {enableQuickPublish && it.status && it.status !== "published" && (
                  <button onClick={() => quickPublish(it)} disabled={publishingId === it.id} className="btn-secondary text-xs px-3 py-2 disabled:opacity-60" data-testid={`content-publish-${it.id}`}>
                    {publishingId === it.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Rocket className="w-3.5 h-3.5" />} {resource === "blog/posts" ? "Publish + Auto" : "Publish"}
                  </button>
                )}
                {enableDuplicate && (
                  <button onClick={() => startDuplicate(it)} className="btn-ghost text-xs px-3 py-2" data-testid={`content-duplicate-${it.id}`}>
                    <Copy className="w-3.5 h-3.5" /> Duplicate
                  </button>
                )}
                <button onClick={() => startEdit(it)} className="btn-ghost text-xs px-3 py-2" data-testid={`content-edit-${it.id}`}>
                  <Edit2 className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => remove(it.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg transition" data-testid={`content-delete-${it.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
